import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DialogFooter } from '@/components/ui/dialog';
import { uploadFile } from '@/lib/api';
import { 
  ChevronRight, ChevronDown, Plus, Pencil, Trash2, FolderOpen, Folder, ImageIcon, X, Loader2
} from 'lucide-react';

// Flatten categories tree into a list with level info and parent tracking
function flattenTree(categories, level, parentId, collapsedSet) {
  const items = [];
  for (const cat of categories) {
    const hasChildren = cat.children && cat.children.length > 0;
    const isCollapsed = collapsedSet.has(cat.node_id);
    items.push({ ...cat, level, hasChildren, isCollapsed });
    if (hasChildren && !isCollapsed) {
      items.push(...flattenTree(cat.children, level + 1, cat.node_id, collapsedSet));
    }
  }
  return items;
}

export const CategoryTreeView = ({ categories, onEdit, onDelete, onAddChild }) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(new Set());

  const toggleCollapse = (nodeId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const flatItems = useMemo(() => flattenTree(categories, 0, null, collapsed), [categories, collapsed]);

  return (
    <div className="space-y-0.5" data-testid="category-tree">
      {flatItems.map((cat) => (
        <div 
          key={cat.node_id}
          className="flex items-center gap-1 py-1.5 px-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 group transition-colors"
          style={{ paddingLeft: `${cat.level * 20 + 8}px` }}
          data-testid={`category-node-${cat.node_id}`}
        >
          <button 
            onClick={() => cat.hasChildren && toggleCollapse(cat.node_id)}
            className="w-5 h-5 flex items-center justify-center shrink-0"
            data-testid={`toggle-category-${cat.node_id}`}
          >
            {cat.hasChildren ? (
              cat.isCollapsed 
                ? <ChevronRight className="h-4 w-4 text-slate-500" /> 
                : <ChevronDown className="h-4 w-4 text-slate-500" />
            ) : (
              <span className="w-4" />
            )}
          </button>

          {cat.hasChildren ? (
            cat.isCollapsed 
              ? <Folder className="h-4 w-4 text-orange-400 shrink-0" /> 
              : <FolderOpen className="h-4 w-4 text-orange-500 shrink-0" />
          ) : (
            <Folder className="h-4 w-4 text-slate-400 shrink-0" />
          )}

          {cat.default_thumbnail_url && (
            <img src={cat.default_thumbnail_url} alt="" className="w-6 h-6 rounded object-cover shrink-0 border" />
          )}

          <span className="flex-1 text-sm font-medium ml-1 truncate">{cat.node_name}</span>

          <div className="hidden group-hover:flex items-center gap-1 shrink-0">
            <Button 
              variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => onAddChild(cat.node_id)}
              title={t('admin.cat.addSubcategory')}
              data-testid={`add-subcategory-${cat.node_id}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            <Button 
              variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => onEdit(cat)}
              title={t('admin.cat.edit')}
              data-testid={`edit-category-${cat.node_id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button 
              variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => onDelete(cat)}
              title={t('admin.cat.delete')}
              data-testid={`delete-category-${cat.node_id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const CategoryForm = ({ flatCategories, initialData, mode, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    node_name: initialData?.node_name || '',
    parent_node_id: initialData?.parent_node_id || '',
    sort_order: initialData?.sort_order || 0,
    default_image_url: initialData?.default_image_url || null,
    default_thumbnail_url: initialData?.default_thumbnail_url || null,
    default_blur_hash: initialData?.default_blur_hash || null
  });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setFormData(prev => ({
        ...prev,
        default_image_url: res.data.file_url,
        default_thumbnail_url: res.data.thumbnail_url,
        default_blur_hash: res.data.blur_hash
      }));
    } catch (err) {
      console.error('Image upload failed:', err);
    }
    setUploading(false);
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, default_image_url: null, default_thumbnail_url: null, default_blur_hash: null }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      <div className="space-y-2">
        <Label>{t('admin.categoryName')}</Label>
        <Input
          value={formData.node_name}
          onChange={(e) => setFormData(prev => ({ ...prev, node_name: e.target.value }))}
          placeholder={t('admin.categoryNamePlaceholder')}
          required
          data-testid="category-name-input"
        />
      </div>
      
      <div className="space-y-2">
        <Label>{t('admin.parentCategory')}</Label>
        <Select
          value={formData.parent_node_id || 'none'}
          onValueChange={(value) => setFormData(prev => ({ ...prev, parent_node_id: value === 'none' ? null : value }))}
        >
          <SelectTrigger data-testid="parent-category-select">
            <SelectValue placeholder={t('admin.noParent')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('admin.noParent')}</SelectItem>
            {flatCategories
              .filter(cat => mode === 'edit' ? cat.node_id !== initialData?.node_id : true)
              .map((cat) => (
                <SelectItem key={cat.node_id} value={cat.node_id}>
                  {'  '.repeat(cat.level)}{cat.node_name}
                </SelectItem>
              ))
            }
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t('admin.sortOrder')}</Label>
        <Input
          type="number"
          value={formData.sort_order}
          onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
          data-testid="category-sort-input"
        />
      </div>

      {/* Default Image Upload */}
      <div className="space-y-2">
        <Label>{t('admin.defaultImage') || 'Standardbild'}</Label>
        {formData.default_thumbnail_url ? (
          <div className="relative inline-block">
            <img src={formData.default_thumbnail_url} alt="" className="w-24 h-24 rounded-lg object-cover border" />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
              data-testid="remove-category-image-btn"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div>
            <label
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors w-fit"
              data-testid="upload-category-image-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
              <span className="text-sm text-muted-foreground">{uploading ? (t('common.uploading') || 'Hochladen...') : (t('admin.uploadDefaultImage') || 'Bild hochladen')}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
            </label>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" className="bg-orange-500 hover:bg-orange-600" data-testid="save-category-btn" disabled={uploading}>
          {mode === 'edit' ? t('common.save') : t('common.create')}
        </Button>
      </DialogFooter>
    </form>
  );
};
